/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Component: NavigationDrawer
 * File     : NavigationDrawer.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Reusable React UI component: NavigationDrawer, composed within pages and other components.
 *   Exports: NavigationDrawer
 *   
 *
 * Dependencies:
 *   - ../../context/AuthContext
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Import: import NavigationDrawer from '@/components/layout/NavigationDrawer';
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home as HomeIcon,
  Add as AddIcon,
  History as HistoryIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const NavigationDrawer = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Allow Header (AppBar) hamburger to toggle this drawer via window events
  useEffect(() => {
    const onToggle = () => setMobileOpen(prev => !prev);
    const onOpen = () => setMobileOpen(true);
    const onClose = () => setMobileOpen(false);
    window.addEventListener('toggle-drawer', onToggle);
    window.addEventListener('open-drawer', onOpen);
    window.addEventListener('close-drawer', onClose);
    return () => {
      window.removeEventListener('toggle-drawer', onToggle);
      window.removeEventListener('open-drawer', onOpen);
      window.removeEventListener('close-drawer', onClose);
    };
  }, []);

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/', show: true },
    { text: 'Create Poll', icon: <AddIcon />, path: '/create', show: !!user },
    // { text: 'Vote', icon: <VoteIcon />, path: '/vote', show: true },
    { text: 'Past Polls', icon: <HistoryIcon />, path: '/history', show: true },
    { text: 'Global Metrics', icon: <HistoryIcon />, path: '/metrics', show: true },
    { text: 'Previous Winners', icon: <HistoryIcon />, path: '/previously-watched', show: !!user },
    { text: 'Clubs', icon: <HistoryIcon />, path: '/clubs', show: !!user },
    { text: 'Rating Opportunities', icon: <HistoryIcon />, path: '/post-view-ratings', show: !!user },
    { text: 'Plex Library', icon: <HistoryIcon />, path: '/plex', show: user?.role === 'owner' || user?.role === 'admin' },
    { text: 'API Docs', icon: <HistoryIcon />, path: '/api-docs', show: user?.role === 'owner' || user?.role === 'admin' },
    { text: 'Pricing', icon: <HistoryIcon />, path: '/pricing', show: true },
    {
      text: 'Discord', icon: (
        <span style={{ display: 'inline-flex', width: 28, height: 28 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" role="img" aria-label="Discord logo" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
        </span>
      ),
      href: 'https://discord.gg/vRrMdZ9dDr',
      external: true,
      show: true,
    },
    // { text: 'Admin', icon: <AdminIcon />, path: '/admin', show: user?.isAdmin },
  ];

  const drawerContent = (
    <div style={{ paddingBottom: 8 }}>
      <List>
        {menuItems
          .filter(item => item.show)
          .map(item => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => {
                  if (item.external && item.href) {
                    window.open(item.href, '_blank', 'noopener,noreferrer');
                  } else if (item.path) {
                    navigate(item.path);
                  }
                  if (isMobile) setMobileOpen(false);
                }}
                selected={!!item.path && location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Divider />
      {user && (
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={logout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      )}
    </div>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240, position: 'relative' },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default NavigationDrawer;
